import GridTable from '../../table/GridTable';
import { ColDef, RowDoubleClickedEvent } from 'ag-grid-community';
import { IConsultOrder, useConsultOrderStore } from '../../../store/useConsultOrder';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { formatDate, updateUrlWithParams } from '../../../util/util';
import { AgGridReact } from 'ag-grid-react';
import queryString from 'query-string';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';

const ConsultOrderFix = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    orders,
    ordersPage,
    getOrderLoading,
    getOrders,
  } = useConsultOrderStore();

  const gridRef = useRef<AgGridReact<IConsultOrder>>(null);
  const [searchName, setSearchName] = useState('');

  const colDefs: ColDef<IConsultOrder>[] = [
    {
      headerName: '가맹점',
      field: 'franchiseName',
      flex: 2,
    },
    {
      headerName: '이름',
      field: 'name',
      flex: 2,
    },
    {
      headerName: '양/음력',
      field: 'solarType',
      flex: 2,
    },
    {
      headerName: '생년월일',
      field: 'birthday',
      flex: 3,
    },
    {
      headerName: '성별',
      field: 'sexType',
      flex: 1,
    },
    {
      headerName: '확정 날짜',
      field: 'confirmDt',
      valueFormatter: param => param.value ? formatDate(param.value).timestamp : '-',
      flex: 3,
    },
    {
      headerName: '결과',
      flex: 1.5,
      cellRenderer: (params: { data: IConsultOrder }) => {
        const order = params.data;
        if (!order?.id) return null;
        return (
          <button
            className="!px-3 !py-1 !text-sm !bg-blue-500 hover:!bg-blue-600 !text-white !rounded !transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/consult-order/fix/${order.id}`);
            }}
          >
            결과 확인
          </button>
        );
      },
    },
  ];

  const onRowDoubleClicked = useCallback(
    (params: RowDoubleClickedEvent<IConsultOrder>) => {
      navigate(`/consult-order/fix/${params.data?.id}`);
    },
    [navigate]
  );

  useEffect(() => {
    const parsedQuery = queryString.parse(location.search);

    let startDt = parsedQuery.startDt as string;
    let endDt = parsedQuery.endDt as string;
    const name = parsedQuery.name as string || '';

    if (!startDt || !endDt) {
      const today = new Date();
      const monthAgo = new Date(today);
      monthAgo.setDate(monthAgo.getDate() - 30);

      startDt = startDt || formatDate(monthAgo)['yyyy-mm-ddTHH:mm:ss'];
      endDt = endDt || formatDate(today)['yyyy-mm-ddTHH:mm:ss'];
    }
    const page = parsedQuery.page ? Number(parsedQuery.page) : 0;
    const size = parsedQuery.size ? Number(parsedQuery.size) : 20;

    // URL의 name 파라미터와 state 동기화
    if (name !== searchName) {
      setSearchName(name);
    }

    getOrders({
      status: 'CONFIRM',
      startDt,
      endDt,
      name,
      page,
      size,
    });
  }, [location.search, getOrders]);

  const handleSearch = useCallback(() => {
    const parsedQuery = queryString.parse(location.search);
    const updatedQuery = { ...parsedQuery, name: searchName, page: '0' };
    updateUrlWithParams(updatedQuery, navigate);
  }, [searchName, location.search, navigate]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  const onPageChange = useCallback(
    (page: number) => {
      const parsedQuery = queryString.parse(location.search);
      const updatedQuery = { ...parsedQuery, page: page.toString() };

      updateUrlWithParams(updatedQuery, navigate);
      getOrders({
        status: 'CONFIRM',
        startDt: parsedQuery.startDt as string,
        endDt: parsedQuery.endDt as string,
        name: parsedQuery.name as string || '',
        page,
        size: parseInt(parsedQuery.size as string) || 20,
      });
    },
    [location.search, navigate, getOrders]
  );

  const rowSelection = {
    mode: 'multiRow',
  };

  return (
    <div className="!w-full !h-full !flex !flex-col">
      <div className="!flex !items-center !justify-between !py-4">
        <div className="!text-lg !font-semibold">
          상담 주문 확정 (총 {ordersPage?.totalElements || 0}건)
        </div>
        <div className="!flex !items-center !gap-2">
          <div className="!relative">
            <input
              type="text"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="이름으로 검색"
              className="!w-48 !pl-3 !pr-10 !py-2 !text-sm !border !border-gray-300 !rounded-lg focus:!outline-none focus:!ring-2 focus:!ring-blue-500 focus:!border-transparent"
            />
            <button
              onClick={handleSearch}
              className="!absolute !right-2 !top-1/2 !transform !-translate-y-1/2 !text-gray-400 hover:!text-gray-600 !transition-colors"
            >
              <FontAwesomeIcon icon={faSearch} className="!w-4 !h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="!flex-1 !min-h-0">
        <GridTable<IConsultOrder>
          gridRef={gridRef}
          height="100%"
          isLoading={getOrderLoading}
          rowData={orders}
          columnDefs={colDefs}
          rowSelection={rowSelection}
          onRowDoubleClicked={onRowDoubleClicked}
        />
      </div>

      {/* 페이지네이션 */}
      {ordersPage && ordersPage.totalPages > 1 && (
        <div className="!w-full !mt-6">
          <div className="!flex !justify-center !items-center !space-x-1">
            <button
              onClick={() => onPageChange(0)}
              disabled={ordersPage.pageable.pageNumber === 0}
              className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3" />
              <FontAwesomeIcon
                icon={faChevronLeft}
                className="!w-3 !h-3 !-ml-1"
              />
            </button>

            <button
              onClick={() =>
                onPageChange(Math.max(0, ordersPage.pageable.pageNumber - 1))
              }
              disabled={ordersPage.pageable.pageNumber === 0}
              className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3" />
            </button>

            <div className="!flex !space-x-0.5">
              {(() => {
                const currentPage = ordersPage.pageable.pageNumber;
                const totalPages = ordersPage.totalPages;
                let startPage = Math.max(0, currentPage - 4);
                let endPage = Math.min(totalPages - 1, startPage + 9);

                if (endPage - startPage < 9) {
                  startPage = Math.max(0, endPage - 9);
                }

                return Array.from(
                  { length: endPage - startPage + 1 },
                  (_, i) => startPage + i
                ).map(pageNum => (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`!px-2.5 !py-1 !text-sm !rounded !transition-colors !duration-200 ${
                      pageNum === currentPage
                        ? '!bg-blue-600 !text-white'
                        : 'hover:!bg-gray-100 !text-gray-700'
                    }`}
                  >
                    {pageNum + 1}
                  </button>
                ));
              })()}
            </div>

            <button
              onClick={() =>
                onPageChange(
                  Math.min(
                    ordersPage.totalPages - 1,
                    ordersPage.pageable.pageNumber + 1
                  )
                )
              }
              disabled={
                ordersPage.pageable.pageNumber === ordersPage.totalPages - 1
              }
              className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
            >
              <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3" />
            </button>

            <button
              onClick={() => onPageChange(ordersPage.totalPages - 1)}
              disabled={
                ordersPage.pageable.pageNumber === ordersPage.totalPages - 1
              }
              className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
            >
              <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3" />
              <FontAwesomeIcon
                icon={faChevronRight}
                className="!w-3 !h-3 !-ml-1"
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultOrderFix;
